class PdpSubscriptionGifts extends HTMLElement {
    constructor() {
        super();
        this.productData = null;
        this.currentVariantId = null;
        this.selectedType = 'subscription';
        this.selectedPlanId = null;
        this.selectedPlanName = '';
        this.form = null;
        this.variantIdInput = null;
        this.quantityInput = null;
        this.optionsContainer = null;
        this.buyOnceNotice = null;
        this.giftsTitleHeading = null;
        this.subscriptionCardTemplate = null;
        this.buyOnceCardTemplate = null;
        this.addToCartButton = null;
        this.isSubmitting = false;
        this.hiddenPlanKeywords = [];
        this.includeGiftsInSubscriptionSection = false;
        this.giftItems = [];
        this.giftsAccordion = null;
        this.giftsContent = null;
        this.isAccordionOpen = true;
        this.accordionOpenByDefault = true;
        this.showSubscriptionPlansAsSelect = false;
        this.showGiftsAccordionAboveCards = false;
        this.showBuyOnceLinkBelowAtc = false;
        this.buyOnceLinkPriceOverride = null;
        this.externalAccordionRoot = null;
        this.externalAccordionContent = null;
        this.buyOnceLinkButton = null;
        this.disableGiftAutoAdd = false;
        this.hideBuyOnceOption = false;
    }

    setPriceText(element, formattedPrice, labelKey) {
        if (!element) return;

        const hiddenLabel = element.querySelector(':scope > .visually-hidden');
        if (hiddenLabel) {
            while (hiddenLabel.nextSibling) {
                hiddenLabel.nextSibling.remove();
            }
            if (formattedPrice) {
                hiddenLabel.insertAdjacentText('afterend', formattedPrice);
            }
            return;
        }

        element.textContent = formattedPrice;
    }

    formatCopy(key, replacements = {}, fallback = '') {
        if (typeof window.Theme?.formatCopy === 'function') {
            return window.Theme.formatCopy(key, replacements, fallback);
        }

        const translations = window.Theme?.translations || {};
        let template = String(translations[key] || fallback).replaceAll('&amp;', '&');

        for (const [token, value] of Object.entries(replacements)) {
            template = template.split(token).join(String(value));
        }

        return template;
    }

    shouldShowExternalGiftsAccordion() {
        return this.includeGiftsInSubscriptionSection && this.showGiftsAccordionAboveCards;
    }

    shouldHidePlan(allocation) {
        if (!this.hiddenPlanKeywords.length) return false;

        const plan = allocation?.selling_plan;
        const planName = String(plan?.name || '').toLowerCase();
        const options = Array.isArray(plan?.options) ? plan.options : [];

        const optionValues = options
            .map((option) => `${String(option?.name || '').toLowerCase()} ${String(option?.value || '').toLowerCase()}`)
            .join(' ');

        const fullText = `${planName} ${optionValues}`.trim();

        return this.hiddenPlanKeywords.some((keyword) => fullText.includes(keyword));
    }

    parsePriceOverride(rawValue) {
        if (!rawValue) return null;
        const numericValue = Number(String(rawValue).replace(/[^0-9.-]/g, ''));
        if (!Number.isFinite(numericValue)) return null;
        return Math.round(numericValue * 100);
    }

    getSubscriptionDiscountPercent(variant) {
        const fromVariant = Number(variant?.subscription_discount);
        if (Number.isFinite(fromVariant) && fromVariant > 0) {
            return fromVariant;
        }

        const fromProduct = Number(this.productData?.subscription_discount);
        if (Number.isFinite(fromProduct) && fromProduct > 0) {
            return fromProduct;
        }

        return 0;
    }

    getSubscriptionDisplay(variant) {
        const otpCents = Number(variant?.price || 0);
        const savingsPercent = this.getSubscriptionDiscountPercent(variant);
        const compareAtCents = Number(variant?.compare_at_price || 0);
        const useComparePrice =
            this.productData?.use_compare_price_new_logic === true &&
            savingsPercent > 0 &&
            compareAtCents > otpCents;
        const discountBaseCents = useComparePrice ? compareAtCents : otpCents;
        const subscriptionCents = savingsPercent > 0
            ? Math.round(discountBaseCents * (100 - savingsPercent) / 100)
            : otpCents;
        const totalSavings = Math.max(discountBaseCents - subscriptionCents, 0);

        return {
            otpCents,
            discountBaseCents,
            subscriptionCents,
            savingsPercent,
            totalSavings,
        };
    }

    mountBuyOnceLink() {
        if (!this.buyOnceLinkButton || !this.addToCartButton) return;

        if (!this.showBuyOnceLinkBelowAtc) {
            this.buyOnceLinkButton.hidden = true;
            return;
        }

        this.addToCartButton.insertAdjacentElement('afterend', this.buyOnceLinkButton);
        this.buyOnceLinkButton.hidden = false;
        this.buyOnceLinkButton.addEventListener('click', async (event) => {
            event.preventDefault();
            await this.handleBuyOnceLinkSubmit();
        });
    }

    connectedCallback() {
        this.optionsContainer = this.querySelector('[data-subscription-options]');
        this.buyOnceNotice = this.querySelector('[data-buy-once-notice]');
        this.giftsTitleHeading = this.querySelector('[data-gifts-title-heading]');
        this.subscriptionCardTemplate = this.querySelector('[data-subscription-card-template]');
        this.buyOnceCardTemplate = this.querySelector('[data-buy-once-card-template]');
        this.form = document.getElementById(this.dataset.productFormId);
        this.includeGiftsInSubscriptionSection = String(this.dataset.includeGiftsInSubscriptionSection) === 'true';
        this.showSubscriptionPlansAsSelect = String(this.dataset.showSubscriptionPlansAsSelect) === 'true';
        this.showGiftsAccordionAboveCards = String(this.dataset.showGiftsAccordionAboveCards) === 'true';
        this.showBuyOnceLinkBelowAtc = String(this.dataset.showBuyOnceLinkBelowAtc) === 'true';
        this.disableGiftAutoAdd = String(this.dataset.disableGiftAutoAdd) === 'true';
        this.hideBuyOnceOption = String(this.dataset.hideBuyOnceOption) === 'true';
        this.accordionOpenByDefault = String(this.dataset.accordionOpenByDefault) === 'true';
        this.isAccordionOpen = this.accordionOpenByDefault;
        this.buyOnceLinkPriceOverride = this.parsePriceOverride(this.dataset.buyOnceLinkPriceOverride);
        this.externalAccordionRoot = this.querySelector('[data-gifts-accordion-external]');
        this.externalAccordionContent = this.querySelector('[data-gifts-content-external]');
        this.buyOnceLinkButton = this.querySelector('[data-buy-once-link]');
        this.hiddenPlanKeywords = String(this.dataset.hiddenPlanKeywords || '')
            .split(',')
            .map((value) => value.trim().toLowerCase())
            .filter(Boolean);

        if (!this.form || !this.optionsContainer || !this.subscriptionCardTemplate || !this.buyOnceCardTemplate) return;

        this.variantIdInput = this.form.querySelector('input[name="id"]');
        this.quantityInput = this.form.querySelector('input[name="quantity"]');
        this.addToCartButton = this.form.querySelector('button[name="add"]');
        this.mountBuyOnceLink();

        const jsonEl = this.querySelector('[data-variant-selling-plans]');
        if (!jsonEl) return;

        try {
            this.productData = JSON.parse(jsonEl.textContent);
        } catch (error) {
            console.error('PDP subscription widget JSON parse error', error);
            return;
        }

        this.currentVariantId = this.getCurrentVariantId();
        this.giftItems = Array.from(document.querySelectorAll('[data-pdp-subscription-gift]'));

        if (this.includeGiftsInSubscriptionSection) {
            this.createGiftsAccordion();
        
            if (this.giftsTitleHeading) {
                this.giftsTitleHeading.style.display = 'none';
            }
        } else {
            if (this.externalAccordionRoot) {
                this.externalAccordionRoot.hidden = true;
            }

            if (this.giftsTitleHeading) {
                this.giftsTitleHeading.style.display = '';
            }
        }

        this.render();
        this.bindEvents();
        this.syncFormState();
        this.updateGiftsState();
        this.updateBuyOnceLinkPrice();
    }

    buildSubscriptionCardWithSelect(plans, variant) {
        const fragment = this.subscriptionCardTemplate.content.cloneNode(true);
        const card = fragment.querySelector('[data-subscription-card]');
    
        const activePlan =
            plans.find((allocation) => String(allocation.selling_plan.id) === String(this.selectedPlanId)) ||
            plans[0];
    
        const planId = String(activePlan.selling_plan.id);
        const planName = activePlan.selling_plan.name || '';
        const { discountBaseCents, subscriptionCents, savingsPercent, totalSavings } = this.getSubscriptionDisplay(variant);
        const comparePrice = discountBaseCents;
        const allocationPrice = subscriptionCents;
    
        const isNewDesign = this.classList.contains('pdp-subscription-gifts--new');
        const formattedSavings = this.formatMoney(totalSavings);
        const saveText = totalSavings > 0
            ? isNewDesign
                ? this.formatCopy(
                    'subscribe_save_amount_every_order',
                    { __AMOUNT__: formattedSavings },
                    `Subscribe & save ${formattedSavings} on every order`
                  )
                : this.formatCopy(
                    'save_amount_every_order',
                    { __AMOUNT__: formattedSavings },
                    `SAVE ${formattedSavings} on every order`
                  )
            : isNewDesign
                ? this.formatCopy('subscribe_save_every_order', {}, 'Subscribe & save on every order')
                : this.formatCopy('save_every_order', {}, 'Save on every order');
    
        card.dataset.planId = planId;
        card.dataset.planName = planName;
    
        const savePercentEl = card.querySelector('[data-card-save-percent]');
        const titleEl = card.querySelector('.subscription-card__title');
        const comparePriceEl = card.querySelector('[data-card-compare-price]');
        const priceEl = card.querySelector('[data-card-price]');
        const saveTextEl = card.querySelector('[data-card-save-text]');
        const deliveryEl = card.querySelector('[data-card-delivery]');
        const planSelectEl = card.querySelector('[data-card-plan-select]');
        const deliveryRowEl = card.querySelector('[data-card-delivery-row]');

        if (titleEl) {
            titleEl.textContent = savingsPercent > 0
                ? this.formatCopy('subscribe_and', {}, 'SUBSCRIBE &')
                : this.formatCopy('subscribe', {}, 'SUBSCRIBE');
        }
    
        if (savePercentEl) {
            if (savingsPercent > 0) {
                savePercentEl.textContent = this.formatCopy(
                    'save_percent',
                    { __PERCENT__: savingsPercent },
                    `SAVE ${savingsPercent}%`
                );
            } else {
                savePercentEl.remove();
            }
        }
    
        if (comparePriceEl) {
            if (savingsPercent > 0) {
                this.setPriceText(comparePriceEl, this.formatMoney(comparePrice, true));
                comparePriceEl.style.display = '';
            } else {
                this.setPriceText(comparePriceEl, '');
                comparePriceEl.style.display = 'none';
            }
        }
    
        if (priceEl) {
            this.setPriceText(priceEl, this.formatMoney(allocationPrice, true));
        }
    
        if (saveTextEl) {
            if (totalSavings > 0) {
                saveTextEl.textContent = saveText;
                saveTextEl.style.display = '';
            } else {
                saveTextEl.textContent = '';
                saveTextEl.style.display = 'none';
            }
        }
    
        if (deliveryEl) {
            deliveryEl.textContent = 'Deliver Every';
        }
    
        if (deliveryRowEl) {
            deliveryRowEl.hidden = false;
            deliveryRowEl.style.display = 'flex';
        }

        if (planSelectEl) {
            const wrapEl = planSelectEl.closest('.subscription-card__delivery-select-wrap');
            const customSelectEl = wrapEl?.querySelector('[data-custom-plan-select]');
            const customDisplayEl = wrapEl?.querySelector('[data-custom-plan-display]');
            const customTextEl = wrapEl?.querySelector('[data-custom-plan-text]');
            const customDropdownEl = wrapEl?.querySelector('[data-custom-plan-dropdown]');

            planSelectEl.innerHTML = '';

            if (customDropdownEl) {
                customDropdownEl.innerHTML = '';
            }

            let selectedLabel = '';

            plans.forEach((allocation) => {
                const currentPlanId = String(allocation.selling_plan.id);
                const label = this.getCustomPlanDisplayLabel(allocation);

                const option = document.createElement('option');
                option.value = currentPlanId;
                option.textContent = label;

                if (currentPlanId === planId) {
                    option.selected = true;
                    selectedLabel = label;
                }

                planSelectEl.appendChild(option);

                if (customDropdownEl) {
                    const item = document.createElement('div');
                    item.className = 'subscription-card__custom-select-item';
                    item.dataset.value = currentPlanId;
                    item.textContent = label;

                    if (currentPlanId === planId) {
                        item.classList.add('is-selected');
                    }

                    item.addEventListener('click', (event) => {
                        event.stopPropagation();

                        const newPlanId = item.dataset.value;
                        const selectedAllocation = plans.find(
                            (allocation) => String(allocation.selling_plan.id) === newPlanId
                        );

                        if (!selectedAllocation) return;

                        planSelectEl.value = newPlanId;

                        this.selectedType = 'subscription';
                        this.selectedPlanId = newPlanId;
                        this.selectedPlanName = selectedAllocation.selling_plan.name || '';

                        if (customTextEl) {
                            customTextEl.textContent = item.textContent;
                        }

                        if (customDropdownEl) {
                            customDropdownEl
                                .querySelectorAll('.subscription-card__custom-select-item')
                                .forEach((el) => el.classList.remove('is-selected'));

                            item.classList.add('is-selected');
                        }

                        if (customSelectEl) {
                            customSelectEl.classList.remove('is-open');
                        }

                        this.render();
                        this.syncFormState();
                        this.updateGiftsState();
                    });

                    customDropdownEl.appendChild(item);
                }
            });

            if (customTextEl) {
                customTextEl.textContent = selectedLabel || (plans?.[0] ? this.getPlanSelectLabel(plans[0]) : '');

            }

            planSelectEl.addEventListener('click', (event) => {
                event.stopPropagation();
            });

            planSelectEl.addEventListener('change', (event) => {
                event.stopPropagation();

                const newPlanId = String(event.target.value);
                const selectedAllocation = plans.find(
                    (allocation) => String(allocation.selling_plan.id) === newPlanId
                );

                if (!selectedAllocation) return;

                this.selectedType = 'subscription';
                this.selectedPlanId = newPlanId;
                this.selectedPlanName = selectedAllocation.selling_plan.name || '';

                if (customTextEl) {
                    const selectedOption = planSelectEl.options[planSelectEl.selectedIndex];
                    customTextEl.textContent = selectedOption ? selectedOption.textContent : '';
                }

                if (customDropdownEl) {
                    customDropdownEl
                        .querySelectorAll('.subscription-card__custom-select-item')
                        .forEach((el) => {
                            el.classList.toggle('is-selected', el.dataset.value === newPlanId);
                        });
                }

                if (customSelectEl) {
                    customSelectEl.classList.remove('is-open');
                }

                this.render();
                this.syncFormState();
                this.updateGiftsState();
            });

            if (customDisplayEl && customSelectEl) {
                customDisplayEl.addEventListener('click', (event) => {
                    event.stopPropagation();
                    customSelectEl.classList.toggle('is-open');
                });
            }

            if (!document.body.dataset.customPlanSelectInit) {
                document.body.dataset.customPlanSelectInit = 'true';

                document.addEventListener('click', () => {
                    document
                        .querySelectorAll('.subscription-card__custom-select.is-open')
                        .forEach((el) => el.classList.remove('is-open'));
                });
            }
        }
    
        return fragment;
    }

    

    createGiftsAccordion() {
        if (this.giftsAccordion) return;
    
        this.giftsAccordion = document.createElement('div');
        this.giftsAccordion.className = 'subscription-card__gifts-accordion-inner';
    
        this.giftsContent = document.createElement('div');
        this.giftsContent.className = 'subscription-card__gifts-content-inner';
    
        this.giftItems.forEach((item) => {
            this.giftsContent.appendChild(item);
        });
    
        this.giftsAccordion.appendChild(this.giftsContent);
    }
    
    moveGiftsAccordionToSelectedCard() {
        if (!this.giftsAccordion) return;

        if (this.shouldShowExternalGiftsAccordion() && this.externalAccordionRoot && this.externalAccordionContent) {
            this.externalAccordionRoot.hidden = false;

            if (!this.externalAccordionContent.contains(this.giftsAccordion)) {
                this.externalAccordionContent.appendChild(this.giftsAccordion);
            }

            const toggle = this.externalAccordionRoot.querySelector('[data-gifts-toggle]');
            if (toggle && !toggle.dataset.bound) {
                toggle.dataset.bound = 'true';
                toggle.addEventListener('click', (event) => {
                    event.preventDefault();
                    this.isAccordionOpen = !this.isAccordionOpen;
                    this.updateAccordionState();
                });
            }

            this.updateAccordionState();
            return;
        }
    
        const selectedSubscriptionCard = this.querySelector(
            '[data-subscription-card][data-type="subscription"].is-selected'
        );
    
        if (!selectedSubscriptionCard) return;
    
        const accordionRoot = selectedSubscriptionCard.querySelector('[data-gifts-accordion]');
        const giftsContentTarget = selectedSubscriptionCard.querySelector('[data-gifts-content]');
        const toggle = selectedSubscriptionCard.querySelector('[data-gifts-toggle]');
    
        if (!accordionRoot || !giftsContentTarget) return;
    
        accordionRoot.hidden = false;
    
        if (!giftsContentTarget.contains(this.giftsAccordion)) {
            giftsContentTarget.appendChild(this.giftsAccordion);
        }
    
        if (toggle && !toggle.dataset.bound) {
            toggle.dataset.bound = 'true';
    
            toggle.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
            
                this.isAccordionOpen = !this.isAccordionOpen;
                this.updateAccordionState();
            });
        }
    
        this.updateAccordionState();
    }
    
    updateAccordionState() {
        let accordionRoot;

        if (this.shouldShowExternalGiftsAccordion()) {
            accordionRoot = this.externalAccordionRoot;
        } else {
            const selectedSubscriptionCard = this.querySelector(
                '[data-subscription-card][data-type="subscription"].is-selected'
            );
            accordionRoot = selectedSubscriptionCard?.querySelector('[data-gifts-accordion]');
        }

        if (!accordionRoot) return;
    
        const toggle = accordionRoot.querySelector('[data-gifts-toggle]');
        const content = this.shouldShowExternalGiftsAccordion()
            ? accordionRoot.querySelector('[data-gifts-content-external]')
            : accordionRoot.querySelector('[data-gifts-content]');
        const chevron = accordionRoot.querySelector('[data-gifts-chevron]');
    
        if (!toggle || !content) return;
    
        toggle.setAttribute('aria-expanded', this.isAccordionOpen ? 'true' : 'false');
        content.style.display = this.isAccordionOpen ? '' : 'none';
    
        if (chevron) {
            chevron.style.transform = this.isAccordionOpen ? 'rotate(180deg)' : 'rotate(0deg)';
        }
    }

    bindEvents() {
        this.optionsContainer.addEventListener('click', (event) => {
            const card = event.target.closest('[data-subscription-card]');
            if (!card) return;

            this.selectedType = card.dataset.type;
            this.selectedPlanId = card.dataset.planId || null;
            this.selectedPlanName = card.dataset.planName || '';

            this.updateSelectedCard();
            this.syncFormState();
            this.updateGiftsState();
        });

        this.optionsContainer.addEventListener('keydown', (event) => {
            const card = event.target.closest('[data-subscription-card]');
            if (!card) return;

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                card.click();
            }
        });

        if (this.variantIdInput) {
            const observer = new MutationObserver(() => {
                const newVariantId = this.getCurrentVariantId();
                if (newVariantId !== this.currentVariantId) {
                    this.currentVariantId = newVariantId;
                    this.render();
                    this.syncFormState();
                    this.updateGiftsState();
                }
            });

            observer.observe(this.variantIdInput, {
                attributes: true,
                attributeFilter: ['value']
            });
        }

        document.addEventListener('variant:update', (event) => {
            const variant = event.detail?.resource;
            if (!variant || !variant.id) return;
            if (String(variant.id) !== String(this.getCurrentVariantId())) return;

            this.currentVariantId = String(variant.id);
            this.render();
            this.syncFormState();
            this.updateGiftsState();
            this.updateBuyOnceLinkPrice();
        });

        document.addEventListener('product-form:variant:update', (event) => {
            const variant = event.detail?.resource;
            if (!variant || !variant.id) return;
            if (String(variant.id) !== String(this.getCurrentVariantId())) return;

            this.currentVariantId = String(variant.id);
            this.render();
            this.syncFormState();
            this.updateGiftsState();
            this.updateBuyOnceLinkPrice();
        });

        if (this.addToCartButton) {
            this.addToCartButton.addEventListener('click', async (event) => {
                const activeWidget = document.querySelector('pdp-subscription-gifts');
                if (activeWidget !== this) return;

                if (this.disableGiftAutoAdd) return;

                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();

                await this.handleSubmit();
            });
        }

        if (this.buyOnceNotice) {
            this.buyOnceNotice.addEventListener('click', () => {
                const variant = this.getCurrentVariantData();
                if (!variant) return;
        
                const allPlans = Array.isArray(variant.selling_plan_allocations)
                    ? variant.selling_plan_allocations
                    : [];
        
                const plans = allPlans.filter((allocation) => !this.shouldHidePlan(allocation));
                if (!plans.length) return;
        
                const firstPlan = plans[0];
                if (!firstPlan?.selling_plan?.id) return;
        
                this.selectedType = 'subscription';
                this.selectedPlanId = String(firstPlan.selling_plan.id);
                this.selectedPlanName = firstPlan.selling_plan.name || '';
        
                this.updateSelectedCard();
                this.syncFormState();
                this.updateGiftsState();
            });
        }
    }

    getGiftItems() {
        return this.giftItems;
    }

    getCurrentVariantId() {
        if (!this.variantIdInput) return String(this.productData?.selected_variant_id || '');
        return String(this.variantIdInput.value || this.productData?.selected_variant_id || '');
    }

    getCurrentVariantData() {
        if (!this.productData?.variants?.length) return null;
        return this.productData.variants.find((variant) => String(variant.id) === String(this.currentVariantId)) || null;
    }
    
    getCustomPlanDisplayLabel(allocation) {
        const sellingPlanName = String(allocation?.selling_plan?.name || '').trim();
        const lowerName = sellingPlanName.toLowerCase();

        if (!lowerName) return '';

        if (lowerName.includes('every ')) {
            const sellingPlanWords = lowerName.split('every ');
            return (sellingPlanWords[1] || '')
                .toUpperCase()
                .replace(' SUBSCRIPTION', '')
                .trim();
        }

        const sellingPlanWords = lowerName.split(' subscription ');
        const firstPart = (sellingPlanWords[0] || '').trim();

        if (
            (firstPart.includes('2') || firstPart.includes('3')) &&
            firstPart.includes('month') &&
            !firstPart.includes('months')
        ) {
            return firstPart
                .replace('month', 'months')
                .toUpperCase()
                .replace(' SUBSCRIPTION', '')
                .trim();
        }

        return firstPart
            .toUpperCase()
            .replace(' SUBSCRIPTION', '')
            .trim();
    }

    getPlanSelectLabel(allocation) {
        const plan = allocation?.selling_plan;
        const options = Array.isArray(plan?.options) ? plan.options : [];
        const planName = String(plan?.name || '').trim();
    
        const intervalOption = options.find((option) => {
            const name = String(option?.name || '').toLowerCase();
            const value = String(option?.value || '').toLowerCase();
    
            return (
                name.includes('delivery') ||
                name.includes('deliver') ||
                name.includes('frequency') ||
                name.includes('interval') ||
                value.includes('month') ||
                value.includes('week')
            );
        });
    
        let rawValue = intervalOption ? String(intervalOption.value || '').trim() : '';
    
        if (!rawValue && planName) {
            rawValue = planName;
        }
    
        if (!rawValue) {
            return 'Subscription';
        }
    
        return rawValue.toUpperCase();
    }

    updateBuyOnceLinkPrice() {
        if (!this.buyOnceLinkButton) return;
        const priceEl = this.buyOnceLinkButton.querySelector('[data-buy-once-link-price]');
        if (!priceEl) return;

        const variant = this.getCurrentVariantData();
        const cents = this.buyOnceLinkPriceOverride != null
            ? this.buyOnceLinkPriceOverride
            : Number(variant?.price || 0);

        priceEl.textContent = this.formatMoney(cents);
    }

    render() {
        const variant = this.getCurrentVariantData();
    
        if (!variant) {
            this.optionsContainer.innerHTML = '';
            return;
        }
    
        const allPlans = Array.isArray(variant.selling_plan_allocations) ? variant.selling_plan_allocations : [];
        const plans = allPlans.filter((allocation) => !this.shouldHidePlan(allocation));
    
        if (plans.length === 0) {
            this.selectedType = 'buy-once';
            this.selectedPlanId = null;
            this.selectedPlanName = '';
        } else {
            const stillExists = plans.some((allocation) => String(allocation.selling_plan.id) === String(this.selectedPlanId));
    
            if (this.selectedType !== 'buy-once' && (!this.selectedPlanId || !stillExists)) {
                this.selectedType = 'subscription';
                this.selectedPlanId = String(plans[0].selling_plan.id);
                this.selectedPlanName = plans[0].selling_plan.name || '';
            }
        }
    
        this.optionsContainer.innerHTML = '';
    
        if (plans.length > 0) {
            if (this.showSubscriptionPlansAsSelect) {
                const card = this.buildSubscriptionCardWithSelect(plans, variant);
                this.optionsContainer.appendChild(card);
            } else {
                plans.forEach((allocation) => {
                    const card = this.buildSubscriptionCard(allocation, variant);
                    this.optionsContainer.appendChild(card);
                });
            }
        }
        const shouldHideBuyOnceCard = this.hideBuyOnceOption && plans.length > 0;
        if (!this.showBuyOnceLinkBelowAtc && !shouldHideBuyOnceCard) {
            const buyOnceCard = this.buildBuyOnceCard(variant);
            this.optionsContainer.appendChild(buyOnceCard);
        }
    
        this.updateSelectedCard();
        this.updateBuyOnceLinkPrice();
    
        if (this.includeGiftsInSubscriptionSection) {
            this.moveGiftsAccordionToSelectedCard();
        }

        this.cloneAddToCartToNewBuybox();
        this.hideOriginalBuyButtons();
    }

    hideOriginalBuyButtons() {
        if (!this.classList.contains('pdp-subscription-gifts--new')) return;
        if (!this.form) return;
    
        const originalButtons = this.form.querySelector('.product-form-buttons');
        if (!originalButtons) return;
    
        originalButtons.classList.add('product-form-buttons--visually-hidden');
    }

    cloneAddToCartToNewBuybox() {
        if (!this.classList.contains('pdp-subscription-gifts--new')) return;
        if (!this.form) return;
    
        const target = this.querySelector('[data-new-buybox-atc-target]');
        if (!target) return;
    
        const originalButtonsWrap = this.form.querySelector('.product-form-buttons');
        if (!originalButtonsWrap) return;
    
        target.innerHTML = '';
    
        const clonedButtonsWrap = originalButtonsWrap.cloneNode(true);
        clonedButtonsWrap.removeAttribute('style');
        clonedButtonsWrap.style.position = '';
        clonedButtonsWrap.style.opacity = '';
        clonedButtonsWrap.style.pointerEvents = '';
        clonedButtonsWrap.style.width = '';
        clonedButtonsWrap.style.height = '';
        clonedButtonsWrap.style.overflow = '';
        clonedButtonsWrap.setAttribute('data-cloned-buy-buttons', 'true');

        clonedButtonsWrap.classList.remove('product-form-buttons--visually-hidden');
    
        const clonedAddButton = clonedButtonsWrap.querySelector('button[name="add"]');
        const originalAddButton = originalButtonsWrap.querySelector('button[name="add"]');
    
        if (clonedAddButton && originalAddButton) {
            clonedAddButton.type = 'button';
            clonedAddButton.removeAttribute('name');
    
            clonedAddButton.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
    
                originalAddButton.click();
            });
        }
    
        target.appendChild(clonedButtonsWrap);
    }

    updateSelectedCard() {
        const cards = this.querySelectorAll('[data-subscription-card]');
    
        cards.forEach((card) => {
            const type = card.dataset.type;
            const planId = card.dataset.planId || '';
            const isSelected =
                type === 'buy-once' && this.selectedType === 'buy-once' ||
                type === 'subscription' &&
                this.selectedType === 'subscription' &&
                String(planId) === String(this.selectedPlanId);
    
            card.classList.toggle('is-selected', isSelected);
            card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });
    
        if (this.buyOnceNotice) {
            this.buyOnceNotice.classList.toggle('hidden', this.selectedType !== 'buy-once');
        }
    
        if (this.includeGiftsInSubscriptionSection) {
            const allAccordions = this.querySelectorAll('[data-gifts-accordion]');
            allAccordions.forEach((accordion) => {
                accordion.hidden = true;
            });

            if (this.externalAccordionRoot) {
                this.externalAccordionRoot.hidden = !this.shouldShowExternalGiftsAccordion();
            }
        
            if (this.selectedType === 'subscription') {
                this.moveGiftsAccordionToSelectedCard();
                this.isAccordionOpen = this.accordionOpenByDefault;
                this.updateAccordionState();
            } else {
                this.isAccordionOpen = false;
                this.updateAccordionState();

                if (this.externalAccordionRoot && this.shouldShowExternalGiftsAccordion()) {
                    this.externalAccordionRoot.hidden = true;
                }
            }
        } else {
            const allAccordions = this.querySelectorAll('[data-gifts-accordion]');
            allAccordions.forEach((accordion) => {
                accordion.hidden = true;
            });

            if (this.externalAccordionRoot) {
                this.externalAccordionRoot.hidden = true;
            }
        }
    }

    buildSubscriptionCard(allocation, variant) {
        const fragment = this.subscriptionCardTemplate.content.cloneNode(true);
        const card = fragment.querySelector('[data-subscription-card]');

        const planId = String(allocation.selling_plan.id);
        const planName = allocation.selling_plan.name || '';
        const { discountBaseCents, subscriptionCents, savingsPercent, totalSavings } = this.getSubscriptionDisplay(variant);
        const comparePrice = discountBaseCents;
        const allocationPrice = subscriptionCents;

        const deliveryText = this.getDeliveryText(allocation);

        const isNewDesign = this.classList.contains('pdp-subscription-gifts--new');
        const formattedSavings = this.formatMoney(totalSavings);
        const saveText = totalSavings > 0
            ? isNewDesign
                ? this.formatCopy(
                    'subscribe_save_amount_every_order',
                    { __AMOUNT__: formattedSavings },
                    `Subscribe & save ${formattedSavings} on every order`
                  )
                : this.formatCopy(
                    'save_amount_every_order',
                    { __AMOUNT__: formattedSavings },
                    `SAVE ${formattedSavings} on every order`
                  )
            : isNewDesign
                ? this.formatCopy('subscribe_save_every_order', {}, 'Subscribe & save on every order')
                : this.formatCopy('save_every_order', {}, 'Save on every order');

        card.dataset.planId = planId;
        card.dataset.planName = planName;

        const savePercentEl = card.querySelector('[data-card-save-percent]');
        const titleEl = card.querySelector('.subscription-card__title');
        const comparePriceEl = card.querySelector('[data-card-compare-price]');
        const priceEl = card.querySelector('[data-card-price]');
        const saveTextEl = card.querySelector('[data-card-save-text]');
        const deliveryEl = card.querySelector('[data-card-delivery-single]');
        const deliveryRowEl = card.querySelector('[data-card-delivery-row]');

        if (deliveryRowEl) {
            deliveryRowEl.hidden = true;
            deliveryRowEl.style.display = 'none';
        }

        if (titleEl) {
            titleEl.textContent = savingsPercent > 0
                ? this.formatCopy('subscribe_and', {}, 'SUBSCRIBE &')
                : this.formatCopy('subscribe', {}, 'SUBSCRIBE');
        }

        if (savePercentEl) {
            if (savingsPercent > 0) {
                savePercentEl.textContent = this.formatCopy(
                    'save_percent',
                    { __PERCENT__: savingsPercent },
                    `SAVE ${savingsPercent}%`
                );
            } else {
                savePercentEl.remove();
            }
        }

        if (comparePriceEl) {
            if (savingsPercent > 0) {
                this.setPriceText(comparePriceEl, this.formatMoney(comparePrice));
                comparePriceEl.style.display = '';
            } else {
                this.setPriceText(comparePriceEl, '');
                comparePriceEl.style.display = 'none';
            }
        }

        if (priceEl) {
            this.setPriceText(priceEl, this.formatMoney(allocationPrice));
        }

        if (saveTextEl) {
            if (totalSavings > 0) {
                saveTextEl.textContent = saveText;
                saveTextEl.style.display = '';
            } else {
                saveTextEl.textContent = '';
                saveTextEl.style.display = 'none';
            }
        }

        if (deliveryEl) {
            deliveryEl.textContent = deliveryText;
        }

        return fragment;
    }

    buildBuyOnceCard(variant) {
        const fragment = this.buyOnceCardTemplate.content.cloneNode(true);
        const card = fragment.querySelector('[data-subscription-card]');
        const titleEl = card.querySelector('[data-buy-once-title]');
        const priceEl = card.querySelector('[data-buy-once-price]');

        if (titleEl) {
            titleEl.textContent = this.dataset.defaultBuyOnceText || 'BUY ONCE';
        }

        if (priceEl) {
            priceEl.textContent = this.formatMoney(Number(variant.price || 0));
        }

        return fragment;
    }

    getDeliveryText(allocation) {
        const plan = allocation?.selling_plan;
        const options = Array.isArray(plan?.options) ? plan.options : [];
        const planName = String(plan?.name || '').trim();

        const intervalOption = options.find((option) => {
            const name = String(option?.name || '').toLowerCase();
            const value = String(option?.value || '').toLowerCase();

            return (
                name.includes('delivery') ||
                name.includes('deliver') ||
                name.includes('frequency') ||
                name.includes('interval') ||
                value.includes('month') ||
                value.includes('week')
            );
        });

        let rawValue = intervalOption ? String(intervalOption.value || '').trim() : '';

        if (!rawValue && planName) {
            rawValue = planName;
        }

        if (!rawValue) {
            return 'SUBSCRIPTION PLAN';
        }

        rawValue = rawValue.replace(/^\d+\s+/, '').trim();

        const match = rawValue.match(/(\d+)\s*[- ]?\s*(day|days|week|weeks|month|months)/i);

        if (match) {
            const count = match[1];
            let unit = match[2].toUpperCase();

            if (count !== '1') {
                if (unit === 'DAY') unit = 'DAYS';
                if (unit === 'WEEK') unit = 'WEEKS';
                if (unit === 'MONTH') unit = 'MONTHS';
            } else {
                if (unit === 'DAYS') unit = 'DAY';
                if (unit === 'WEEKS') unit = 'WEEK';
                if (unit === 'MONTHS') unit = 'MONTH';
            }

            return `DELIVER EVERY ${count} ${unit}`;
        }

        return rawValue.toUpperCase();
    }

    updateGiftsState() {
        const giftItems = this.getGiftItems();
    
        giftItems.forEach((item) => {
            const unavailableRaw = item.dataset.unavailablePlans || '';
            const unavailablePlans = unavailableRaw
                .split(',')
                .map((entry) => entry.trim().toLowerCase())
                .filter(Boolean);
    
            let locked = false;
    
            if (this.selectedType === 'buy-once') {
                locked = true;
            } else if (unavailablePlans.length > 0) {
                const currentPlanName = (this.selectedPlanName || '').toLowerCase();
                locked = unavailablePlans.some((entry) => currentPlanName.includes(entry));
            }
    
            item.classList.toggle('is-locked', locked);
            item.setAttribute('aria-disabled', locked ? 'true' : 'false');
    
            if (!this.includeGiftsInSubscriptionSection) {
                item.style.display = this.selectedType === 'buy-once' ? 'none' : '';
            }
        });
    
        if (this.includeGiftsInSubscriptionSection) {
            if (this.externalAccordionRoot) {
                this.externalAccordionRoot.hidden = !this.shouldShowExternalGiftsAccordion() || this.selectedType !== 'subscription';
            }
        } else if (this.giftsTitleHeading) {
            this.giftsTitleHeading.style.display = this.selectedType === 'buy-once' ? 'none' : '';
        }
    }

    syncFormState() {
        if (!this.form) return;

        let sellingPlanInput = this.form.querySelector('input[name="selling_plan"][data-pdp-subscription-widget]');
        if (!sellingPlanInput) {
            sellingPlanInput = document.createElement('input');
            sellingPlanInput.type = 'hidden';
            sellingPlanInput.name = 'selling_plan';
            sellingPlanInput.setAttribute('data-pdp-subscription-widget', 'true');
            this.form.appendChild(sellingPlanInput);
        }

        if (this.selectedType === 'subscription' && this.selectedPlanId) {
            sellingPlanInput.value = this.selectedPlanId;
        } else {
            sellingPlanInput.value = '';
        }
        let subscriptionOnlyInput = this.form.querySelector('input[name="properties[_subscription_only]"][data-pdp-subscription-widget]');
        const shouldSetSubscriptionOnlyAttribute = this.hideBuyOnceOption && this.selectedType === 'subscription';

        if (shouldSetSubscriptionOnlyAttribute) {
            if (!subscriptionOnlyInput) {
                subscriptionOnlyInput = document.createElement('input');
                subscriptionOnlyInput.type = 'hidden';
                subscriptionOnlyInput.name = 'properties[_subscription_only]';
                subscriptionOnlyInput.setAttribute('data-pdp-subscription-widget', 'true');
                this.form.appendChild(subscriptionOnlyInput);
            }

            subscriptionOnlyInput.value = 'true';
        } else if (subscriptionOnlyInput) {
            subscriptionOnlyInput.remove();
        }
    }

    getUnlockedGiftVariantIds() {
        if (this.selectedType !== 'subscription') return [];

        return this.getGiftItems()
            .filter((item) => !item.classList.contains('is-locked'))
            .map((item) => item.dataset.variantId)
            .filter(Boolean);
    }

    async handleSubmit() {
        if (this.isSubmitting) return;
        this.isSubmitting = true;

        const submitButton = this.form.querySelector('button[name="add"]');
        const originalText = submitButton ? submitButton.textContent : '';

        try {
            const shouldRedirectToCheckout =
                document.querySelector('[data-redirect-to-checkout="true"]') !== null;

            if (shouldRedirectToCheckout) {
                document.body.classList.add('hide-recharge-storefront-experiences');
            }

            if (submitButton) {
                submitButton.disabled = true;
            }

            const variantId = this.getCurrentVariantId();
            const quantity = Number(this.quantityInput?.value || 1);
            const items = [];

            const mainItem = {
                id: Number(variantId),
                quantity
            };

            if (this.selectedType === 'subscription' && this.selectedPlanId) {
                mainItem.selling_plan = Number(this.selectedPlanId);
            }

            items.push(mainItem);

            if (!this.disableGiftAutoAdd) {
                this.getUnlockedGiftVariantIds().forEach((giftVariantId) => {
                    items.push({
                        id: Number(giftVariantId),
                        quantity: 1
                    });
                });
            }

            const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ items })
            });

            if (!response.ok) {
                throw new Error('Failed to add items to cart');
            }
            
            if (shouldRedirectToCheckout) {
                window.location.href = '/checkout';
                return;
            }

            if(!shouldRedirectToCheckout){
                const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
                    headers: {
                        'Accept': 'application/json'
                    }
                });

                const cart = await cartResponse.json();

                document.dispatchEvent(
                    new CustomEvent('cart:update', {
                        bubbles: true,
                        detail: {
                            resource: cart,
                            sourceId: this.dataset.blockId || 'pdp-subscription-gifts',
                            data: {
                                source: 'pdp-subscription-gifts',
                                itemCount: items.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
                                variantId: String(variantId)
                            }
                        }
                    })
                );
                const cartDrawer = document.querySelector('cart-drawer-component');

                if (cartDrawer && typeof cartDrawer.open === 'function') {
                    cartDrawer.open();
                }
            }

        } catch (error) {
            console.error('PDP subscription submit error', error);
            alert('There was an error adding the item to cart.');
        } finally {
            this.isSubmitting = false;

            if (submitButton) {
                submitButton.disabled = false;
                if (originalText) {
                    submitButton.textContent = originalText;
                }
            }
        }
    }

    async handleBuyOnceLinkSubmit() {
        if (this.isSubmitting || !this.buyOnceLinkButton) return;
        this.isSubmitting = true;

        const originalHtml = this.buyOnceLinkButton.innerHTML;

        try {
            this.buyOnceLinkButton.disabled = true;
            this.buyOnceLinkButton.textContent = 'Added to Cart!';

            const shouldRedirectToCheckout =
                document.querySelector('[data-redirect-to-checkout="true"]') !== null;

            const variantId = this.getCurrentVariantId();
            const quantity = Number(this.quantityInput?.value || 1);

            const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    items: [{
                        id: Number(variantId),
                        quantity
                    }]
                })
            });

            if (!response.ok) {
                throw new Error('Failed to add buy once item');
            }

            if (shouldRedirectToCheckout) {
                window.location.href = '/checkout';
                return;
            }

            const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
                headers: {
                    'Accept': 'application/json'
                }
            });

            const cart = await cartResponse.json();
            document.dispatchEvent(
                new CustomEvent('cart:update', {
                    bubbles: true,
                    detail: {
                        resource: cart,
                        sourceId: this.dataset.blockId || 'pdp-subscription-gifts',
                        data: {
                            source: 'pdp-subscription-gifts-buy-once-link',
                            itemCount: quantity,
                            variantId: String(variantId)
                        }
                    }
                })
            );

            const cartDrawer = document.querySelector('cart-drawer-component');
            if (cartDrawer && typeof cartDrawer.open === 'function') {
                cartDrawer.open();
            }
        } catch (error) {
            console.error('PDP subscription buy once link error', error);
            alert('There was an error adding the item to cart.');
        } finally {
            this.isSubmitting = false;
            this.buyOnceLinkButton.disabled = false;
            this.buyOnceLinkButton.innerHTML = originalHtml;
            this.updateBuyOnceLinkPrice();
        }
    }

   formatMoney(cents, disableCents) {
        const amount = Number(cents || 0) / 100;

        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
            return window.Shopify.formatMoney(cents, this.dataset.moneyFormat || '${{amount}}');
        }

        if (disableCents && amount % 1 === 0) {
            return `$${amount.toFixed(0)}`;
        }

        return `$${amount.toFixed(2)}`;
    }
}

if (!customElements.get('pdp-subscription-gifts')) {
    customElements.define('pdp-subscription-gifts', PdpSubscriptionGifts);
}
